import { Folder, localDB } from "@/database/db";
import { useState } from "react";
import {
  COLORS,
  DeckEditingDialog,
  MoveDeckFolderDialog,
} from "../personalization/deck-editing";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  ArrowUpDownIcon,
  FolderEdit,
  FolderOpenIcon,
  FolderPenIcon,
  FolderPlusIcon,
  HomeIcon,
  Loader2Icon,
  MoreHorizontalIcon,
  PenBoxIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { TooltipWithShortcuts } from "@/components/tooltips";
import { getCustomizedColor } from "./folder-color-utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { FullFoldersSubtree } from "../sidebar/sidebar";
import { LocalDeck } from "@/lib/decks-utils/utils";
import { usePostHog } from "posthog-js/react";

export const NewFolderDialog = ({
  editFolder,
  displayLabel,
  parentFolderId,
}: {
  editFolder?: Folder;
  alwaysVisible?: boolean;
  displayLabel?: boolean;
  parentFolderId?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const posthog = usePostHog();
  const [folderName, setFolderName] = useState(editFolder?.name || "");
  const [selectedColor, setSelectedColor] = useState<string>(
    editFolder?.color || COLORS[0]
  );
  const [params] = useSearchParams();
  const parentFolder = parentFolderId || params.get("folder");
  const isSubFolder = !!parentFolderId;

  if (parentFolderId === "unassigned") {
    return <></>;
  }

  return (
    <>
      {editFolder ? (
        <Button
          variant={displayLabel ? "secondary" : "strokeSecondary"}
          size={displayLabel ? "sm" : "icon"}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setIsOpen(true);
          }}
        >
          <FolderPenIcon className="w-icon-md h-icon-md" />
          {displayLabel ? <span className="ml-1">Edit Folder</span> : ""}
        </Button>
      ) : (
        <Button
          variant={displayLabel ? "secondary" : "strokeSecondary"}
          size={displayLabel ? "sm" : isSubFolder ? "icon2xs" : "iconMd"}
          onClick={() => setIsOpen(true)}
        >
          <FolderPlusIcon
            className={cn(
              isSubFolder ? "h-icon-sm w-icon-sm" : "h-icon-md w-icon-md"
            )}
          />{" "}
          {displayLabel ? (
            <span className="hidden sm:block ml-1">Add Folder</span>
          ) : (
            ""
          )}
        </Button>
      )}
      <Dialog open={isOpen} onOpenChange={(change) => setIsOpen(change)}>
        <DialogContent className="max-w-[730px] text-subtitle-800 p-0 gap-0">
          <DialogHeader className="flex gap-2 items-center justify-start flex-row text-title-950 font-semibold p-5 border-b border-secondary-200">
            <FolderOpenIcon className="h-icon-lg w-icon-lg over" />
            {editFolder
              ? "Edit folder"
              : `${isSubFolder ? "Add Sub-Folder" : "Create a new folder"}`}
          </DialogHeader>
          <form
            id="new-folder-form"
            onSubmit={async (e) => {
              e.preventDefault();
              if (editFolder) {
                posthog.capture("Folder-Edit-Folder");
              } else {
                if (parentFolderId) {
                  posthog.capture("Folder-Create-Subfolder");
                } else {
                  posthog.capture("Folder-Create-Folder");
                }
              }

              await localDB.updateFolder({
                name: folderName,
                color: selectedColor,
                id: editFolder?.id,
                archivedTimeISO: undefined,
                parentFolder:
                  editFolder?.parentFolder || parentFolder || undefined,
              });
              setIsOpen(false);
            }}
            className="flex flex-col gap-8 p-6 overflow-y-auto"
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="name" className="font-semibold">
                Name
              </Label>
              <Input
                id="name"
                value={folderName}
                onChange={(e) => {
                  setFolderName(e.target.value);
                }}
                placeholder="My new Folder! 📁"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="thumbnail" className="font-semibold">
                {isSubFolder ? "Sub-Folder Color" : "Folder Color"}
              </Label>
              <div className="grid grid-cols-6 gap-3">
                {COLORS.slice(0, -1).map((color, index) => (
                  <TooltipWithShortcuts
                    key={index}
                    text={color}
                    className="capitalize"
                  >
                    <div
                      key={index}
                      className={cn(
                        `w-100% h-[64px] rounded-xl cursor-pointer`,
                        getCustomizedColor(color),
                        selectedColor === color
                          ? `border-2 border-secondary-700`
                          : ""
                      )}
                      onClick={() => {
                        setSelectedColor(color);
                      }}
                    />
                  </TooltipWithShortcuts>
                ))}
              </div>
            </div>
          </form>
          <DialogFooter className="p-4 shrink-0 border-t border-secondary-200">
            <div className="w-full flex justify-end gap-3">
              <Button type="button" size="lg" onClick={() => setIsOpen(false)}>
                <XIcon className="h-5 w-5" />
                Cancel
              </Button>
              <Button
                type="submit"
                form="new-folder-form"
                variant="primary"
                size="lg"
                disabled={folderName.length === 0}
              >
                {editFolder ? (
                  <>
                    <FolderEdit className="h-5 w-5 mr-2" /> Save Changes
                  </>
                ) : (
                  <>
                    <FolderPlusIcon className="h-5 w-5 mr-2" />
                    {isSubFolder ? "Add Sub-Folder" : "Create Folder"}
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const EditFolderDialog = ({
  editFolder,
  children,
}: {
  editFolder: Folder;
  children?: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [folderName, setFolderName] = useState(editFolder.name);

  const [selectedColor, setSelectedColor] = useState<string>(
    editFolder?.color || COLORS[0]
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          {children ?? (
            <Button
              variant="secondary"
              size="iconMd"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsOpen(true);
              }}
            >
              <FolderPenIcon className="h-5 w-5" />
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="max-w-[730px] text-subtitle-800 p-0 gap-0">
          <DialogHeader className="flex gap-2 items-center justify-start flex-row text-title-950 font-semibold p-5 border-b border-secondary-200">
            <FolderOpenIcon className=" h-icon-lg w-icon-lg" />
            {editFolder ? "Edit the folder" : "Create a new folder"}
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              await localDB.updateFolder({
                ...editFolder,
                name: folderName,
                color: selectedColor,
              });
              setIsOpen(false);
            }}
            id="edit-folder-form"
            className="flex flex-col gap-8 p-6 overflow-y-auto"
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="name" className="font-semibold">
                Name
              </Label>
              <Input
                id="name"
                value={folderName}
                onChange={(e) => {
                  setFolderName(e.target.value);
                }}
                placeholder="My new Folder! 📁"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="thumbnail" className="font-semibold">
                Color
              </Label>
              <div className="grid grid-cols-6 gap-3">
                {COLORS.slice(0, -1).map((color, index) => (
                  <TooltipWithShortcuts
                    key={index}
                    text={color}
                    className="capitalize"
                  >
                    <div
                      key={index}
                      className={cn(
                        `w-full h-16 rounded-xl cursor-pointer`,
                        getCustomizedColor(color),
                        selectedColor === color
                          ? `border-2 border-secondary-700`
                          : ""
                      )}
                      onClick={() => {
                        setSelectedColor(color);
                      }}
                    />
                  </TooltipWithShortcuts>
                ))}
              </div>
            </div>
          </form>
          <DialogFooter className="p-4 shrink-0 border-t border-secondary-200">
            <div className="w-full flex justify-end gap-3">
              <Button size="lg" onClick={() => setIsOpen(false)}>
                <XIcon className="h-5 w-5" />
                Cancel
              </Button>
              <Button
                size="lg"
                type="submit"
                variant="primary"
                form="edit-folder-form"
                disabled={folderName.length === 0}
              >
                {editFolder ? (
                  <>
                    <FolderEdit className="h-5 w-5 mr-2" /> Edit Folder
                  </>
                ) : (
                  <>
                    <FolderPlusIcon className="h-5 w-5 mr-2" /> Create Folder
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const MoveFolderDialog = ({
  editFolder,
  children,
  folders,
  localDecks,
  folderId,
}: {
  editFolder: Folder;
  children?: React.ReactNode;
  folders: Folder[];
  localDecks: LocalDeck[];
  folderId: Folder["id"] | null;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [selectedParent, setSelectedParent] = useState<Folder["id"] | null>(
    editFolder.parentFolder || null
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          {children ?? (
            <Button
              variant="secondary"
              size="iconMd"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsOpen(true);
              }}
            >
              <ArrowUpDownIcon className="h-5 w-5" />
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="max-h-[90dvh] p-0 gap-0">
          <div className="items-center justify-center text-subtitle-800 max-h-[80dvh] overflow-y-auto p-5 mt-12">
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-0 h-full overflow-y-auto">
                <Button
                  variant="strokeSecondarySidebar"
                  size="sm"
                  className={cn(
                    "w-full p-2 my-0.5 text-xs items-center justify-start flex-shrink-0"
                  )}
                  onClick={() => setSelectedParent(null)}
                >
                  <HomeIcon className="mr-1" />
                  Home
                </Button>
                <FullFoldersSubtree
                  folders={folders}
                  localDecks={localDecks}
                  folderId={folderId}
                  onClickFolder={(folderId) => {
                    setSelectedParent(folderId);
                  }}
                  hideDecks
                  expandAll
                />
              </div>
            </div>
          </div>
          <DialogFooter className="p-4 shrink-0 border-t border-secondary-200 !justify-center">
            <MoveFolderConfirmationButton
              editFolder={editFolder}
              newParent={selectedParent}
              closeDialog={() => setIsOpen(false)}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const MoveFolderConfirmationButton = ({
  editFolder,
  newParent,
  closeDialog,
}: {
  editFolder: Folder;
  newParent: Folder["id"] | null;
  children?: React.ReactNode;
  closeDialog: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  return (
    <Button
      type="submit"
      variant="primary"
      disabled={newParent === editFolder.id || isLoading}
      onClick={async () => {
        setIsLoading(true);
        await localDB.moveFolder(editFolder.id, newParent);
        closeDialog();
        setIsLoading(false);
      }}
    >
      {isLoading ? (
        <Loader2Icon className="h-5 w-5 animate-spin" />
      ) : (
        <>
          <ArrowUpDownIcon className="h-5 w-5 mr-1" />
          Move Folder
        </>
      )}
    </Button>
  );
};

export const DeleteFolderDialog = ({
  folder,
  children,
}: {
  folder: Folder;
  children?: React.ReactNode;
}) => {
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const [isDialogFolderDeletionOpen, setIsDialogFolderDeletionOpen] =
    useState(false);

  return (
    <Dialog
      open={isDialogFolderDeletionOpen}
      onOpenChange={(status) => setIsDialogFolderDeletionOpen(status)}
    >
      <DialogTrigger asChild>
        {children ?? (
          <Button variant="secondary" size="iconMd">
            <Trash2Icon className="h-5 w-5" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-[400px] items-center justify-center">
        <DialogTitle>Are you sure you want to delete this folder?</DialogTitle>
        <div>
          This action is not reversable.
          <br />
          The decks be moved to the parent folder
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => setIsDialogFolderDeletionOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="alert"
            onClick={async () => {
              const parentFolderId = folder.parentFolder;
              await localDB.deleteFolder(folder.id);
              navigate(
                pathname + (parentFolderId ? "?folder=" + parentFolderId : "")
              );
              setIsDialogFolderDeletionOpen(false);
            }}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DropdownFolderDialog = ({
  folder,
  folders,
  localDecks,
  folderId,
  iconSize = "iconMd",
  onOpenChange,
}: {
  folder: Folder;
  folders: Folder[];
  localDecks: LocalDeck[];
  folderId: Folder["id"] | null;
  iconSize?: "icon2xs" | "iconMd";
  onOpenChange?: (open: boolean) => void;
}) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  if (folder.id === "unassigned") {
    return <></>;
  }

  return (
    <>
      <DropdownMenu onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          {/* Using MoreVertical as a common trigger icon */}
          <Button variant="strokeSecondarySidebar" size={iconSize}>
            <MoreHorizontalIcon className="h-4 w-4 mt-[2px] text-subtitle-800" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className={cn(iconSize === "icon2xs" ? "z-[250]" : "z-[50]")}
        >
          <EditFolderDialog editFolder={folder}>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
              }}
            >
              <FolderPenIcon className="mr-2 h-4 w-4" />
              <span>Edit Folder</span>
            </DropdownMenuItem>
          </EditFolderDialog>
          <MoveFolderDialog
            editFolder={folder}
            folders={folders}
            localDecks={localDecks}
            folderId={folderId}
          >
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <ArrowUpDownIcon className="mr-2 h-4 w-4" />
              <span>Move Folder</span>
            </DropdownMenuItem>
          </MoveFolderDialog>
          <DeleteFolderDialog folder={folder}>
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600 focus:bg-red-100"
              onSelect={(e) => {
                e.preventDefault(); // Prevent dropdown to avoid being thrown out of dialog
              }}
            >
              <Trash2Icon className="h-5 w-5 mr-2" />
              <span>Delete</span>
            </DropdownMenuItem>
          </DeleteFolderDialog>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit Dialog controlled by state */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        {/* Reusing NewFolderDialog for editing, passing the folder */}
        {/* Note: We need to pass the folder and manage open state */}
        <DialogContent className="max-w-[400px] items-center justify-center text-subtitle-800">
          <DialogHeader className="flex gap-2 items-center justify-start flex-row text-title-950 font-semibold">
            <FolderOpenIcon className=" h-icon-lg w-icon-lg" /> Edit the folder
          </DialogHeader>
          <div className="border border-secondary-100  -ml-10 w-[120%]"></div>
          <NewFolderDialog editFolder={folder} alwaysVisible />
        </DialogContent>
      </Dialog>
    </>
  );
};

export const DropdownDeckDialog = ({
  deck,
  folders,
  iconSize = "iconMd",
  onOpenChange,
}: {
  deck: LocalDeck;
  folders: Folder[];
  iconSize?: "icon2xs" | "iconMd";
  onOpenChange?: (open: boolean) => void;
}) => {
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  return (
    <>
      <DropdownMenu onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          {/* Using MoreVertical as a common trigger icon */}
          <Button variant="strokeSecondarySidebar" size={iconSize}>
            <MoreHorizontalIcon className="h-4 w-4 mt-[2px] text-subtitle-800" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className={cn(iconSize === "icon2xs" ? "z-[250]" : "z-[50]")}
        >
          <MoveDeckFolderDialog
            deckToMove={isMoveDialogOpen ? deck : undefined}
            folders={folders || []}
            close={() => {
              setIsMoveDialogOpen(false);
            }}
          >
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setIsMoveDialogOpen(true);
              }}
            >
              <ArrowUpDownIcon className="mr-2 h-4 w-4" />
              <span>Move Deck</span>
            </DropdownMenuItem>
          </MoveDeckFolderDialog>
          <DeckEditingDialog
            deckToEdit={isEditDialogOpen ? deck : undefined}
            close={() => setIsEditDialogOpen(false)}
          >
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setIsEditDialogOpen(true);
              }}
            >
              <PenBoxIcon className="mr-2 h-4 w-4" />
              <span>Edit Deck</span>
            </DropdownMenuItem>
          </DeckEditingDialog>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
